import {useContext, useState} from "react";
import {WidgetAccordionRows} from "../../organisms/widget--accordion-rows";
import {Dictionary} from "../../../types/hygiena-types";
import {DictionaryContext} from "../../../context/dictionary-context";
import Fuse from "fuse.js";
import {DocumentPagination} from "../../organisms/widget--document-search";
import {DrupalNode, DrupalTaxonomyTerm} from "next-drupal";
import {getBindingIdentifiers} from "@babel/types";
import keys = getBindingIdentifiers.keys;

export function ParagraphSPAWithSearchFilter({paragraph}) {
  const t = useContext<Dictionary>(DictionaryContext);
  const [page, setPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(30);
  const [event, setEvent] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [year, setYear] = useState<string>("");
  const [query, setQuery] = useState("");

  if (!paragraph?.field_spa_swf_items?.length) return <></>

  // Sort by category before applying filters.
  let searchedPosters = paragraph?.field_spa_swf_items?.sort((a: DrupalNode, b: DrupalNode) => {
    if (a?.field_sp_category?.name < b?.field_sp_category?.name) return -1;
    if (a?.field_sp_category?.name > b?.field_sp_category?.name) return 1;
    return 0;
  });

  const changePage = (page: number) => setPage(page);

  // Apply query search.
  const fuse = new Fuse(paragraph?.field_spa_swf_items, {
    keys: [
      "title",
      "field_sp_event.title",
      "field_sp_category.name",
    ],
    threshold: 0.3,
  });
  if (query.length) {
    const results = fuse.search(query);
    searchedPosters = results.map((result) => result.item);
  }

  // Filter the posters out by the current filters.
  searchedPosters = searchedPosters.filter((item: DrupalNode) => {
    let itemYear = item?.field_sp_start_time?.split('-')[0] ?? "";
    if (category && item?.field_sp_category?.name !== category) {
      return;
    }
    if (event && item?.field_sp_event?.title !== event) {
      return;
    }
    if (year && year !== itemYear) {
      return;
    }
    return item;
  })

  // Set to the current page.
  // let currentDocuments = searchedPosters.slice(page * pageLimit, (page * pageLimit) + pageLimit);
  let currentDocuments = searchedPosters;

  function handleSearch({currentTarget = {}}) {
    const {value} = currentTarget as HTMLInputElement;
    setQuery(value);
  }

  const categories = getCategories();
  const categorizedPosters = getCategorizedPosters();
  const documentCount = getDocumentCount();

  /**
   * Get all available categories.
   * @todo this is duped from getCategorizedPosters, clean up as necessary.
   */
  function getCategories() {
    const categories = {};

    for (const item of paragraph?.field_spa_swf_items) {
      const itemCategory = item?.field_sp_category?.name ?? "";
      const itemYear = item?.field_sp_start_time?.split('-')[0] ?? "";
      const itemPoster = item?.currentDocuments;
      if (event?.length && item.field_sp_event !== event) {
        continue;
      }
      if (category?.length && itemCategory !== category) {
        continue;
      }
      if (year?.length && itemYear !== year) {
        continue;
      }

      if (!categories?.[itemCategory]) {
        categories[itemCategory] = [];
      }
      if (!categories?.[itemCategory]) {
        categories[itemPoster] = [];
      }
      categories[itemCategory].push(item);
    }

    return categories;
  }

  /**
   * Gets the categorized posters.
   */
  function getCategorizedPosters() {
    const categories = {};

    for (const item of currentDocuments) {
      const itemCategory = item?.field_sp_category?.name ?? "";
      const itemYear = item?.field_sp_start_time?.split('-')[0] ?? "";
      const itemPoster = item?.currentDocuments;
      if (event?.length && item.field_sp_event?.title !== event) {
        continue;
      }
      if (category?.length && itemCategory !== category) {
        continue;
      }
      if (year?.length && itemYear !== year) {
        continue;
      }

      if (!categories?.[itemCategory]) {
        categories[itemCategory] = [];
      }
      if (!categories?.[itemCategory]) {
        categories[itemPoster] = [];
      }
      categories[itemCategory].push(item);
    }

    return categories;
  }

  /**
   * Get the current document count.
   */
  function getDocumentCount() {
    let count = 0;
    for (const item of searchedPosters) {
      const itemCategory = item?.field_sp_category?.name ?? "";
      const itemYear = item?.field_sp_start_time?.split('-')[0] ?? "";
      if (event?.length && item.field_sp_event !== event) continue;
      if (category?.length && itemCategory !== category) continue;
      if (year?.length && itemYear !== year) continue;
      count++;
    }
    return count;
  }


  function getEvents(): string[] {
    const events = {};
    for (const item of paragraph?.field_spa_swf_items) {
      const event = item.field_sp_event;
      if (!event) {
        continue;
      }
      if (!events?.[event.title]) {
        events[event.title] = [];
      }

      events[event.title].push(item);
    }
    return Object.keys(events);
  }

  function getYears(): string[] {
    const years: string[] = [];
    for (const item of paragraph?.field_spa_swf_items) {
      const year = item?.field_sp_start_time?.split('-')[0] ?? "";
      if (year?.length && !years.includes(year)) {
        years.push(year);
      }
    }
    return years;
  }

  return (
    <div className="grid gap-2">
      <form className="bg-white flex md:flex-row p-6 md:gap-6 text-black items-center mb-4">

        <label className="font-semibold">{`${t.all.search}`}<input onChange={handleSearch} value={query} type="text"
                                                      id="facet--document_search" placeholder={`${t.documents.search_documents}`}
                                                      className="block px-4 py-2 border-hygienaGray border rounded-sm font-normal mt-2 w-full md:max-w-[90%] lg:min-w-[400px] lg:mr-6"/></label>

        {getEvents().length < 1 ? null : (
          <label className="font-semibold w-full max-md:w-full items-center">
            {`${t.all.events}`}
            <select onChange={event => {
              setEvent(event.target.value !== "0" ? event.target.value : "");
              setPage(0)
            }} className="block px-4 py-2 border-hygienaGray border rounded-sm font-normal mt-2 w-full">
              <option value="0">{`${t.all.all_events}`}</option>
              {getEvents().map((event, index) => (
                <option key={index} value={event}>{event}</option>
              ))}
            </select>
          </label>
        )}
        <label className="font-semibold w-full max-md:w-full items-center">
          {`${t.documents.category}`}
          <select onChange={cat => {
            setCategory(cat.target.value !== "0" ? cat.target.value : "");
            setPage(0)
          }} className="block px-4 py-2 border-hygienaGray border rounded-sm font-normal mt-2 w-full">
            <option value="0">{`${t.documents.all_categories}`}</option>
            {Object.keys(categories).sort().map(key => (
              <option key={key}>{key}</option>
            ))}
          </select>
        </label>

        {getYears().length < 1 ? null : (
          <label className="font-semibold w-full max-md:w-full items-center">
            {`${t.all.year}`}
            <select onChange={event => {
              setYear(event.target.value !== "0" ? event.target.value : "");
              setPage(0)
            }} className="block px-4 py-2 border-hygienaGray border rounded-sm font-normal mt-2 w-full">
              <option value="0"> {`${t.all.all_years}`}</option>
              {getYears().map((year, index) => (
                <option key={index} value={year}>{year}</option>
              ))}
            </select>
          </label>
        )}
      </form>

      {Object.keys(categorizedPosters).sort().map(key => (
        <WidgetAccordionRows key={`${paragraph.id}-${key}`} title={key} content={categorizedPosters[key]}/>
      ))}

      {/* @todo removing pagination for now - retain in case this needs to be added back later - 06/10/2024 */}
      {/*<div className="flex flex-col md:flex-row gap-6 justify-left items-center px-6 bg-white py-6 justify-between">*/}
      {/*  <label htmlFor="document-search--per-page" className="hidden md:inline">*/}
      {/*    {`${t?.documents?.show}`}*/}
      {/*    <select id="document-search--per-page" name="per_page"*/}
      {/*            onChange={(event) => {*/}
      {/*              setPage(0);*/}
      {/*              setPageLimit(parseInt(event.target.value));*/}
      {/*            }}*/}
      {/*            value={pageLimit}*/}
      {/*            className="px-2 py-1 border border-black rounded-sm mx-2">*/}
      {/*      <option value="10">10</option>*/}
      {/*      <option value="20">20</option>*/}
      {/*      <option value="30">30</option>*/}
      {/*      <option value="40">40</option>*/}
      {/*    </select>*/}
      {/*    {`${t?.documents?.per_page}`}*/}
      {/*  </label>*/}
      {/*  <div*/}
      {/*    className="max-md:self-baseline lowercase">{documentCount} {documentCount > 1 || documentCount === 0 ? `${t?.documents?.documents}` : `${t?.documents?.document}`}</div>*/}
      {/*  <DocumentPagination count={documentCount} page={page} limit={pageLimit} changePage={changePage} radius={8}/>*/}
      {/*</div>*/}

    </div>
  );
}